import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { doc, setDoc, getDoc } from 'firebase/firestore';

const StripeConfig = ({ canManagePayments }) => {
  const [config, setConfig] = useState({
    publishableKey: '',
    secretKey: '',
    webhookSecret: ''
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    if (canManagePayments) {
      loadConfig();
    }
  }, [canManagePayments]);

  const loadConfig = async () => {
    setLoading(true);
    try {
      const configDoc = await getDoc(doc(db, 'config', 'stripe'));
      if (configDoc.exists()) {
        setConfig(configDoc.data());
      }
    } catch (error) {
      console.error('Erreur lors du chargement de la configuration:', error);
      setMessage({ type: 'error', text: 'Erreur lors du chargement de la configuration' });
    } finally {
      setLoading(false);
    }
  };

  const saveConfig = async () => {
    if (!canManagePayments) return;

    setSaving(true);
    setMessage(null);

    try {
      // Validation basique
      if (!config.publishableKey.startsWith('pk_')) {
        setMessage({ type: 'error', text: 'Clé publique Stripe invalide (doit commencer par pk_)' });
        return;
      }

      if (!config.secretKey.startsWith('sk_')) {
        setMessage({ type: 'error', text: 'Clé secrète Stripe invalide (doit commencer par sk_)' });
        return;
      }

      if (!config.webhookSecret.startsWith('whsec_')) {
        setMessage({ type: 'error', text: 'Secret webhook Stripe invalide (doit commencer par whsec_)' });
        return;
      }

      await setDoc(doc(db, 'config', 'stripe'), config);
      setMessage({ type: 'success', text: 'Configuration Stripe sauvegardée avec succès' });
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      setMessage({ type: 'error', text: 'Erreur lors de la sauvegarde de la configuration' });
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field, value) => {
    setConfig(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const testConnection = async () => {
    if (!canManagePayments) return;

    setLoading(true);
    setMessage(null);

    try {
      // Test basique de la configuration
      if (!config.publishableKey || !config.secretKey) {
        setMessage({ type: 'error', text: 'Veuillez d\'abord configurer les clés Stripe' });
        return;
      }

      // Ici, vous pourriez ajouter un appel API pour tester la connexion Stripe
      // Pour l'instant, nous faisons juste une validation basique
      
      setMessage({ type: 'success', text: 'Configuration Stripe valide (test basique réussi)' });
    } catch (error) {
      console.error('Erreur lors du test:', error);
      setMessage({ type: 'error', text: 'Erreur lors du test de connexion Stripe' });
    } finally {
      setLoading(false);
    }
  };

  if (!canManagePayments) {
    return (
      <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg p-4">
        <p className="text-yellow-800 dark:text-yellow-300">
          Accès réservé aux utilisateurs avec la permission de gestion des paiements
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          Configuration Stripe
        </h2>
        <p className="text-gray-600 dark:text-gray-400">
          Configurez les clés API Stripe pour activer les paiements d'abonnement
        </p>
      </div>

      {/* Message de statut */}
      {message && (
        <div className={`mb-6 p-4 rounded-lg ${
          message.type === 'success'
            ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 text-green-800 dark:text-green-300'
            : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 text-red-800 dark:text-red-300'
        }`}>
          {message.text}
        </div>
      )}

      {/* Formulaire de configuration */}
      <div className="space-y-6">
        {/* Clé publique */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Clé publique Stripe (Publishable Key)
          </label>
          <input
            type="text"
            value={config.publishableKey}
            onChange={(e) => handleInputChange('publishableKey', e.target.value)}
            placeholder="pk_live_..."
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
          />
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Commence par "pk_live_" pour la production ou "pk_test_" pour les tests
          </p>
        </div>

        {/* Clé secrète */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Clé secrète Stripe (Secret Key)
          </label>
          <input
            type="password"
            value={config.secretKey}
            onChange={(e) => handleInputChange('secretKey', e.target.value)}
            placeholder="sk_live_..."
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
          />
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Commence par "sk_live_" pour la production ou "sk_test_" pour les tests
          </p>
        </div>

        {/* Secret webhook */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Secret Webhook Stripe
          </label>
          <input
            type="password"
            value={config.webhookSecret}
            onChange={(e) => handleInputChange('webhookSecret', e.target.value)}
            placeholder="whsec_..."
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
          />
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Commence par "whsec_" - Récupérable dans le dashboard Stripe → Developers → Webhooks
          </p>
        </div>

        {/* Instructions webhook */}
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4">
          <h3 className="font-semibold text-blue-800 dark:text-blue-300 mb-2">Configuration Webhook</h3>
          <p className="text-blue-700 dark:text-blue-300 text-sm mb-2">
            Dans le dashboard Stripe, configurez le webhook avec cette URL :
          </p>
          <code className="bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 px-2 py-1 rounded text-sm block mb-2">
            https://us-central1-{process.env.REACT_APP_FIREBASE_PROJECT_ID}.cloudfunctions.net/handleStripeWebhook
          </code>
          <p className="text-blue-700 dark:text-blue-300 text-sm">
            Événements à écouter : invoice.payment_succeeded, invoice.payment_failed, customer.subscription.updated, customer.subscription.deleted
          </p>
        </div>

        {/* Boutons d'action */}
        <div className="flex space-x-4 pt-4">
          <button
            onClick={testConnection}
            disabled={saving}
            className="flex-1 py-2 px-4 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
          >
            Tester la connexion
          </button>
          <button
            onClick={saveConfig}
            disabled={saving}
            className="flex-1 py-2 px-4 bg-blue-600 dark:bg-blue-500 text-white rounded-md hover:bg-blue-700 dark:hover:bg-blue-600 disabled:opacity-50"
          >
            {saving ? (
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Sauvegarde...
              </div>
            ) : (
              'Sauvegarder'
            )}
          </button>
        </div>
      </div>

      {/* Informations de sécurité */}
      <div className="mt-8 border-t dark:border-gray-700 pt-6">
        <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Informations de sécurité</h3>
        <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-2">
          <li className="flex items-start">
            <svg className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            Les clés sont stockées de manière sécurisée dans Firestore
          </li>
          <li className="flex items-start">
            <svg className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            Seuls les utilisateurs autorisés peuvent accéder à cette configuration
          </li>
          <li className="flex items-start">
            <svg className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            Utilisez les clés de test (test mode) pour les développements
          </li>
        </ul>
      </div>
    </div>
  );
};

export default StripeConfig;
